import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../components/ui/simple-toast';
import { 
  Building2, 
  MapPin, 
  Plus, 
  Pencil, 
  Trash2, 
  Phone, 
  MapPinned,
  Crown,
  Store
} from 'lucide-react';

const Branches = () => {
  const { api, user } = useAuth();
  const { branches, refreshBranches, currentBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    phone: ''
  });

  const isAdmin = user?.role === 'administrador';

  const openNewModal = () => {
    setEditingBranch(null);
    setForm({ name: '', address: '', phone: '' });
    setShowModal(true);
  };

  const openEditModal = (branch) => {
    setEditingBranch(branch);
    setForm({
      name: branch.name,
      address: branch.address || '',
      phone: branch.phone || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setLoading(true);
    try {
      if (editingBranch) {
        await api.put(`/branches/${editingBranch.branch_id}`, form);
        toast.success('Sucursal actualizada');
      } else {
        await api.post('/branches', form);
        toast.success('Sucursal creada');
      }
      setShowModal(false);
      refreshBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar sucursal');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (branch) => {
    if (branch.is_matriz) {
      toast.error('No se puede eliminar la Matriz');
      return;
    }

    if (!window.confirm(`¿Desactivar la sucursal "${branch.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/branches/${branch.branch_id}`);
      toast.success('Sucursal desactivada');
      refreshBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar sucursal');
    }
  };

  return (
    <>
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-4 md:p-8 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold flex items-center gap-2">
                <Building2 className="w-8 h-8 text-accent" />
                Sucursales
              </h1>
              <p className="text-muted-foreground">
                Administra las sucursales de tu restaurante
              </p>
            </div>
            
            {isAdmin && (
              <Button 
                className="bg-accent text-white hover:bg-accent/90"
                onClick={openNewModal}
                data-testid="new-branch-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Sucursal
              </Button>
            )}
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 flex items-center gap-2">
              <Store className="w-5 h-5" />
              ¿Cómo funcionan las sucursales?
            </h3>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• <strong>Matriz:</strong> Realiza compras y traspasa insumos a las sucursales</li>
              <li>• <strong>Sucursales:</strong> Reciben insumos y venden productos</li>
              <li>• <strong>Reportes:</strong> Cada sucursal tiene sus propios reportes separados</li>
              <li>• <strong>Selector:</strong> Usa el selector en la barra superior para cambiar de sucursal</li>
            </ul>
          </div>

          {/* Branches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((branch) => (
              <div 
                key={branch.branch_id}
                className={`bg-white rounded-xl border-2 p-5 transition-all hover:shadow-md ${
                  branch.is_matriz 
                    ? 'border-accent bg-accent/5' 
                    : currentBranch?.branch_id === branch.branch_id
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-border'
                } ${branch.is_active === false ? 'opacity-50' : ''}`}
                data-testid={`branch-card-${branch.branch_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    branch.is_matriz 
                      ? 'bg-accent/20 text-accent' 
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {branch.is_matriz ? (
                      <Crown className="w-6 h-6" />
                    ) : (
                      <MapPin className="w-6 h-6" />
                    )}
                  </div>
                  
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditModal(branch)}
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!branch.is_matriz && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(branch)}
                          title="Desactivar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <h3 className="font-bold text-lg mb-1">{branch.name}</h3>
                
                {branch.is_matriz && (
                  <span className="inline-block px-2 py-0.5 text-xs font-medium bg-accent/20 text-accent rounded-full mb-2">
                    Principal - Compras y Traspasos
                  </span>
                )}

                {branch.address && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
                    <MapPinned className="w-3.5 h-3.5" />
                    {branch.address}
                  </p>
                )}

                {branch.phone && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Phone className="w-3.5 h-3.5" />
                    {branch.phone}
                  </p>
                )}

                {branch.is_active === false && (
                  <p className="text-xs text-destructive mt-2 font-medium">
                    Sucursal desactivada
                  </p>
                )}

                {currentBranch?.branch_id === branch.branch_id && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-accent font-medium flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Sucursal activa actualmente
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {branches.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay sucursales configuradas</p>
              <p className="text-sm">Las sucursales predeterminadas se crearán automáticamente</p>
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre de la Sucursal *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Sucursal Centro"
                required
                data-testid="branch-name-input"
              />
            </div>

            <div>
              <Label>Dirección (opcional)</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Ej: Av. Principal #123"
                data-testid="branch-address-input"
              />
            </div>

            <div>
              <Label>Teléfono (opcional)</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Ej: 555-123-4567"
                data-testid="branch-phone-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-accent text-white"
                disabled={loading}
              >
                {loading ? 'Guardando...' : editingBranch ? 'Guardar Cambios' : 'Crear Sucursal'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Branches;
